import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  useLoaderData,
  useSearchParams,
  redirect,
  useNavigation,
  useNavigate,
} from 'react-router-dom';
import {
  VaLoadingIndicator,
  VaBreadcrumbs,
  VaAlert,
  VaAdditionalInfo,
  VaButtonSegmented,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { useFeatureToggle } from '~/platform/utilities/feature-toggles/useFeatureToggle';
import api from '../utilities/api';
import { SEARCH_BC_LABEL, poaSearchBC } from '../utilities/poaRequests';
import {
  SEARCH_PARAMS,
  SORT_BY,
  STATUSES,
  PENDING_SORT_DEFAULTS,
  PROCESSED_SORT_DEFAULTS,
} from '../utilities/constants';
import { recordDatalayerEvent } from '../utilities/analytics';
import SortForm from '../components/SortForm';
import Pagination from '../components/Pagination';
import PaginationMeta from '../components/PaginationMeta';
import POARequestSearchPageResults from '../components/POARequestSearchPageResults';

const NoResultsText = searchStatus => (
  <div className="no-poa-requests-text">
    {searchStatus.searchStatus === STATUSES.PENDING
      ? 'No pending representation requests.'
      : 'No processed representation requests.'}
  </div>
);

const RepresentationRequestsAdditionalInfo = () => (
  <VaAdditionalInfo
    trigger="Receiving and reviewing requests in the portal"
    uswds
    data-testid="representation-requests-additional-info"
  >
    <div className="poa-request__additional-info">
      <p>Here’s how to receive requests in the portal:</p>
      <ul>
        <li>
          The Veterans Service Organization (VSO) has to activate the
          Representation Requests feature for their organization,{' '}
          <strong>and</strong>
        </li>
        <li>
          Claimants need to submit requests using the online{' '}
          <va-link
            href="https://www.va.gov/get-help-from-accredited-representative/appoint-rep/introduction/"
            text="VA Form 21-22 (on VA.gov)"
          />
        </li>
      </ul>
      <p>
        The VSO controls whether you can review details for all of their
        requests, or only the requests where you are the preferred
        representative. Follow your organization’s policy about which requests
        you can accept or decline.
      </p>
      <p className="vads-u-margin-bottom--0">
        <a href="https://www.va.gov/representative/help#establishing-representation">
          Learn more about establishing representation
        </a>
      </p>
    </div>
  </VaAdditionalInfo>
);

const POARequestSearchPage = title => {
  const { TOGGLE_NAMES, useToggleValue } = useFeatureToggle();
  const isRepInitiatedPoaEnabled = useToggleValue(
    TOGGLE_NAMES.appointARepresentativeEnableRepInitiatedPoa,
  );
  const [searchParams] = useSearchParams();
  useEffect(() => {
    document.title = title.title;
  }, [title]);
  const loaderData = useLoaderData() || {};
  const poaRequests = loaderData.data || [];
  const meta =
    loaderData.meta && loaderData.meta.page
      ? loaderData.meta
      : { page: { total: 0, number: 1, totalPages: 1 } };
  const { showPOA403Alert } = loaderData;
  const searchStatus = searchParams.get('status');
  const selectedIndividual = searchParams.get('show');
  const navigation = useNavigation();
  const navigate = useNavigate();
  const statusPrefix =
    searchStatus === STATUSES.PENDING ? 'Pending' : 'Processed';
  const sortDefaults =
    searchStatus === STATUSES.PENDING
      ? PENDING_SORT_DEFAULTS
      : PROCESSED_SORT_DEFAULTS;

  return (
    <section className="poa-request">
      <VaBreadcrumbs
        breadcrumbList={poaSearchBC}
        label={SEARCH_BC_LABEL}
        homeVeteransAffairs={false}
        wrapping
      />
      <h1
        data-testid="representation-requests-heading"
        className="poa-request__search-header"
      >
        Representation requests
      </h1>
      <p className="poa-request__copy">
        This list shows representation requests that have been received in the
        portal over the last 60 days.
      </p>
      <RepresentationRequestsAdditionalInfo />

      {showPOA403Alert && (
        <>
          <br />
          <VaAlert status="info" uswds visible data-testid="poa-403-info-alert">
            <h2 slot="headline">This feature hasn’t been activated</h2>
            <div className="vads-u-margin-y--0">
              <p className="vads-u-margin-bottom--2">
                Veterans Service Organizations (VSOs) have to activate the
                Representation Requests feature for their organization if they
                want to receive requests in the portal. If you’d like your VSO
                to activate this feature, ask the VSO manager or certifying
                official to email us at{' '}
                <a href="mailto:RepresentativePortalHelp@va.gov">
                  RepresentativePortalHelp@va.gov
                </a>
                .
              </p>
              <p className="vads-u-margin-bottom--0">
                <strong>Note:</strong> This feature is currently not available
                to claims agents or attorneys. It may become available to them
                in the future.
              </p>
            </div>
          </VaAlert>
        </>
      )}

      <div className="representation-requests-page-table-container">
        {isRepInitiatedPoaEnabled && (
          <p>
            <va-link
              href="/representative/representation-requests/appoint-rep"
              text="Start a representation request for a claimant"
            />
          </p>
        )}
        <VaButtonSegmented
          className="vads-u-margin-y--4 vads-u-align-self--flex-start"
          buttons={[
            {
              label: 'Pending',
              value: STATUSES.PENDING,
            },
            {
              label: 'Processed',
              value: STATUSES.PROCESSED,
            },
          ]}
          onVaButtonClick={event => {
            recordDatalayerEvent(event);
            navigate(
              `?status=${event.detail.value}&sort=${
                searchParams.get(SEARCH_PARAMS.SORT) || SORT_BY.NEWEST
              }&perPage=20&page=1&show=${selectedIndividual}`,
            );
          }}
          selected={searchStatus === STATUSES.PENDING ? 0 : 1}
        />
        <h2
          data-testid="representation-requests-table-heading"
          className="poa-request__tab-heading"
        >
          {`${statusPrefix} representation requests`}
        </h2>
        <SortForm
          options={[
            {
              sort: 'newest',
              label: 'Newest',
            },
            {
              sort: 'oldest',
              label: 'Oldest',
            },
          ]}
          defaults={sortDefaults}
        />
        {(() => {
          switch (searchStatus) {
            case STATUSES.PENDING:
              return (
                <>
                  {(meta.page.total > 0 ||
                    (selectedIndividual && selectedIndividual !== 'false')) && (
                    <PaginationMeta
                      meta={meta}
                      results={poaRequests}
                      resultType="requests"
                      defaults={PENDING_SORT_DEFAULTS}
                    />
                  )}
                </>
              );
            case STATUSES.PROCESSED:
              return (
                <>
                  {(meta.page.total > 0 ||
                    (selectedIndividual && selectedIndividual !== 'false')) && (
                    <PaginationMeta
                      meta={meta}
                      results={poaRequests}
                      resultType="requests"
                      defaults={PROCESSED_SORT_DEFAULTS}
                    />
                  )}
                </>
              );
            default:
              throw new Error(`Unexpected status: ${searchStatus}`);
          }
        })()}
        {navigation.state === 'loading' ? (
          <VaLoadingIndicator message="Loading..." />
        ) : (
          <div className={searchStatus}>
            {meta.page.total === 0 && (
              <NoResultsText searchStatus={searchStatus} />
            )}

            {meta.page.total >= 1 && (
              <POARequestSearchPageResults poaRequests={poaRequests} />
            )}
          </div>
        )}
        <Pagination meta={meta} defaults={PENDING_SORT_DEFAULTS} />
      </div>
    </section>
  );
};

POARequestSearchPage.propTypes = {
  title: PropTypes.string,
};

POARequestSearchPage.loader = async ({ request }) => {
  const { searchParams } = new URL(request.url);
  const status = searchParams.get(SEARCH_PARAMS.STATUS);
  const sort = searchParams.get(SEARCH_PARAMS.SORT);
  const size = searchParams.get(SEARCH_PARAMS.SIZE);
  const number = searchParams.get(SEARCH_PARAMS.NUMBER);
  const selectedIndividual = searchParams.get(
    SEARCH_PARAMS.SELECTED_INDIVIDUAL,
  );
  if (
    !Object.values(STATUSES).includes(status) &&
    !Object.values(STATUSES).includes(sort)
  ) {
    searchParams.set(SEARCH_PARAMS.STATUS, STATUSES.PENDING);
    searchParams.set(SEARCH_PARAMS.SORT, SORT_BY.NEWEST);
    searchParams.set(SEARCH_PARAMS.SIZE, PENDING_SORT_DEFAULTS.SIZE);
    searchParams.set(SEARCH_PARAMS.NUMBER, PENDING_SORT_DEFAULTS.NUMBER);
    searchParams.set(
      SEARCH_PARAMS.SELECTED_INDIVIDUAL,
      PENDING_SORT_DEFAULTS.SELECTED_INDIVIDUAL,
    );
    throw redirect(`?${searchParams}`);
  }

  try {
    return await api.getPOARequests(
      { status, sort, size, number, selectedIndividual },
      {
        signal: request.signal,
        skip403Redirect: true,
      },
    );
  } catch (err) {
    if (err instanceof Response && err.status === 403) {
      // Try authorization endpoint
      try {
        await api.checkAuthorized({
          signal: request.signal,
        });
        // If authorized as a representative, show alert and empty data
        return { data: [], meta: {}, showPOA403Alert: true };
      } catch (authErr) {
        // If not authorized, let the redirect/throw happen as usual
        throw err;
      }
    }
    throw err;
  }
};

export default POARequestSearchPage;
